import _ from 'lodash';
import { ReviewLevel } from '@scale/shared-types';
import React, { FC, useEffect, useState } from 'react';
import {
  ChatStoreContextProvider,
  useCreateChatStore,
} from 'react-scale/components/scalers/tasks/Chat/hooks/ChatStore';
import { ChatTaskContext } from 'react-scale/components/scalers/tasks/ThreadedInteraction/contexts/ChatTaskContext';
import { ChatInteraction } from 'react-scale/components/scalers/tasks/ThreadedInteraction/ChatInteraction';
import { useStore } from 'zustand';
import { useChatLayoutStore } from 'react-scale/components/scalers/tasks/ThreadedInteraction/hooks/useChatLayoutStore';
import { useWindowSize, useMeasure } from 'react-use-v13';
import { feedbackBarWidth } from './layoutHelpers';
import { useWorkerCommentsStore } from 'react-scale/components/scalers/tasks/ThreadedInteraction/hooks/useWorkerCommentsStore';
import API from '@scale/frontend-lib/dashboard-api';
import { ToastContainer } from 'react-toastify';
import { ChatBulkAuditChatRendererProps } from './chatBulkAuditChatRendererProps';
import { useCommonChatTaskContextConfigs } from 'react-scale/components/scalers/tasks/Chat/hooks/configs';
import useUnleashFeatureFlag from 'react-scale/hooks/useUnleashFeatureFlag';
import { ScrollArea } from '@scale/scaleui-radix';
import ConfirmModal from './ConfirmModal';
import { ChatRendererHeader } from './ChatRendererHeader';
import { useTranslationManagement } from './hooks/useTranslationManagement';
import { useEditManagement } from './hooks/useEditManagement';
import {
  withChatBulkAuditErrorBoundary,
  useErrorBoundarySetup,
} from './ChatBulkAuditErrorBoundary';
import { useQuery } from '@tanstack/react-query-v4';

/**
 * V1 Chat Renderer - handles TaskType.Chat
 */
const ChatBulkAuditChatRendererV1Internal: FC<ChatBulkAuditChatRendererProps> = React.memo(
  props => {
    useErrorBoundarySetup(props.auditAttemptId);

    // Chat store setup
    const chatStore = useCreateChatStore(
      {
        config: props.params,
        reviewLevel: props.assignment?.reviewLevel,
        hasActiveStepResponseLinterError: false,
        responses: Array.isArray(props.responses) ? props.responses : [],
        customerAuditShowInstructions: false,
        isRealCustomerAudit: true,
      },
      undefined,
      'ChatAttempt - ChatStore',
    );
    const responses = useStore(chatStore.current, state => state.responses);

    // Translation management
    const {
      showTranslatedResponses,
      translatedTask,
      translatedResponses,
      translatedParams,
      isLoadingTranslatingResponses,
      handleTranslationToggle,
    } = useTranslationManagement({
      responses,
      params: props.params,
      readOnly: props.readOnly,
      taskAttemptId: props.renderedTaskAttemptId,
      auditAttemptId: props.auditAttemptId,
    });

    // Edit management
    const {
      handleSubmitRevert,
      handleConfirmRevert,
      handleCancelRevert,
      handleResponsesChange,
      showConfirmRevertModal,
      revertCount,
      debouncedSubmitEdit,
    } = useEditManagement({
      auditAttemptId: props.auditAttemptId,
      originalResponses: props.responses,
      readOnly: props.readOnly,
    });

    const setIsRealCustomerAudit = useStore(
      chatStore.current,
      state => state.setIsRealCustomerAudit,
    );
    const setWorkerComments = useWorkerCommentsStore(state => state.setWorkerComments);

    const { data: workerCommentsData } = useQuery({
      queryKey: ['workerComments', props.reviewedTaskAttemptId],
      queryFn: async () => {
        if (!props.reviewedTaskAttemptId) {
          return { workerComments: [] };
        }
        return await API.genai.fetchWorkerCommentsForTaskAttempt({
          taskAttemptId: props.reviewedTaskAttemptId,
        });
      },
      enabled: !!props.reviewedTaskAttemptId,
    });

    // Update worker comments store when data changes
    useEffect(() => {
      const workerComments = workerCommentsData?.workerComments ?? [];
      setWorkerComments(workerComments);
    }, [workerCommentsData, setWorkerComments]);

    // Layout management
    const { layout } = useChatLayoutStore();
    const { width: windowWidth } = useWindowSize();
    const [ref, { width: divWidth }] = useMeasure();

    // Key for re-rendering chat interaction
    const assignmentId = props.assignment?.assignmentId || props._id || props.id;
    const taskAttemptId = props.renderedTaskAttemptId;
    const reviewLevel = props.assignment?.reviewLevel ?? ReviewLevel.Attempt;
    const [chatInteractionKey, setChatInteractionKey] = useState<string>(
      `${assignmentId}-${taskAttemptId}-${reviewLevel}`,
    );

    // Update store when props change
    useEffect(() => {
      const config = showTranslatedResponses && translatedParams ? translatedParams : props.params;
      const responsesData =
        showTranslatedResponses && translatedResponses ? translatedResponses : props.responses;
      const responses = Array.isArray(responsesData) ? responsesData : [];

      const completedSteps = responses.reduce(
        (acc, response) => ({
          ...acc,
          [response.index]: true,
        }),
        {} as Record<number, boolean>,
      );

      chatStore.current.setState({
        config,
        reviewLevel: props.assignment?.reviewLevel ?? ReviewLevel.Attempt,
        responses,
        completedSteps,
      });

      setChatInteractionKey(
        `${props.assignment?.assignmentId}-${props.renderedTaskAttemptId}-${
          props.assignment?.reviewLevel ?? ReviewLevel.Attempt
        }-${revertCount}-${showTranslatedResponses ? 'translated' : 'original'}`,
      );
    }, [
      props.auditAttemptId,
      props.renderedTaskAttemptId,
      props.assignment?.assignmentId,
      props.assignment?.reviewLevel,
      props.responses,
      props.params,
      revertCount,
      showTranslatedResponses,
      translatedResponses,
      translatedParams,
      chatStore,
    ]);

    // Handle response changes for auto-save
    useEffect(() => {
      if (!props.readOnly && !_.isEqual(responses, props.responses)) {
        handleResponsesChange(responses);
      }
      return () => {
        debouncedSubmitEdit.cancel();
      };
    }, [responses, props.responses, handleResponsesChange, props.readOnly, debouncedSubmitEdit]);

    // Update read-only state
    useEffect(() => {
      setIsRealCustomerAudit(props.readOnly);
    }, [props.readOnly, setIsRealCustomerAudit]);

    // Feature flags and configs
    const chatTaskFeatureConfig = props.assignment?.projectConfig?.chatTaskFeatureConfig;
    const commonChatTaskContextConfigs = useCommonChatTaskContextConfigs(
      props.params,
      props.assignment?.reviewLevel,
      chatTaskFeatureConfig,
      props.params.templateVariables,
    );

    const isInitialAttempt =
      !!props.benchmark?.isAttempterBenchmark ||
      props.assignment?.reviewLevel === ReviewLevel.Attempt;

    const shouldUseUnifiedFeedback = useUnleashFeatureFlag(
      'chat-enable-contributor-feedback',
      { projectId: props.project },
      false,
    );

    const shouldUseAutoQCResults = useUnleashFeatureFlag(
      'chat-enable-autoqc-results',
      { projectId: props.project },
      false,
    );

    return (
      <div className="scaleui scale-tailwind scale-theme w-full h-full flex flex-col flex-nowrap bg-utility-white">
        <ToastContainer />
        <div
          className={
            'flex w-full grow overflow-auto ' +
            (layout === 'multicolumn' || divWidth + feedbackBarWidth > windowWidth
              ? 'justify-start'
              : 'justify-center')
          }
        >
          <div className="h-full flex flex-col bg-utility-white" ref={ref}>
            <ChatStoreContextProvider store={chatStore}>
              <ChatTaskContext.Provider
                value={{
                  assignmentId,
                  taskAttemptIdWithFeedback: taskAttemptId,
                  projectId: props.project,
                  workerId: props.loggedUserId,
                  ...commonChatTaskContextConfigs,
                  chatBenchmarkConfig: _.get(props.assignment, 'metadata.chatBenchmarkConfig'),
                  isInitialAttempt,
                  canEditComments: false,
                  isPipelineV2: !props.isPipelineV3HumanNode && !props.isTestQuestion,
                  disableLinter: props.isTaxonomyDisplay ? true : undefined,
                  enableUnifiedFeedback: shouldUseUnifiedFeedback || shouldUseAutoQCResults,
                  contextSource: 'ChatBulkAudit',
                }}
              >
                <ChatRendererHeader
                  readOnly={props.readOnly}
                  edited={props.edited}
                  editedStepList={props.editedStepList}
                  showTranslatedResponses={showTranslatedResponses}
                  isLoadingTranslatingResponses={isLoadingTranslatingResponses}
                  responses={props.responses}
                  translatedTask={translatedTask}
                  onRevertEdits={handleSubmitRevert}
                  onTranslationToggle={handleTranslationToggle}
                />

                <ScrollArea className="flex-1 min-h-0 px-8" size="1" type="hover">
                  <div className="w-full">
                    <ChatInteraction
                      key={chatInteractionKey}
                      readOnly={props.readOnly}
                      enableLayoutOverride
                    />
                  </div>
                </ScrollArea>
              </ChatTaskContext.Provider>
            </ChatStoreContextProvider>
          </div>
        </div>

        {showConfirmRevertModal && (
          <ConfirmModal
            isOpen={showConfirmRevertModal}
            onClose={handleCancelRevert}
            onConfirm={handleConfirmRevert}
            message="Reverted changes cannot be recovered. Do you wish to proceed?"
            actionButtonVariant="danger"
            actionButtonText="Revert"
          />
        )}
      </div>
    );
  },
);

export const ChatBulkAuditChatRendererV1 = withChatBulkAuditErrorBoundary(
  ChatBulkAuditChatRendererV1Internal,
);
